// Shared codec for the pawn-ticket QR code.
//
// The QR encodes a URL of the form:
//   <origin>/pawn/ticket-view?d=<base64url(JSON)>
//
// The ticket data is packed into a compact object with short keys to keep the
// URL (and therefore the QR) small enough to scan reliably. Both the generator
// (PawnTicketGenerator) and the viewer (PawnTicketView) import from here so the
// two sides can never drift out of sync.

import LZString from "lz-string";

// Make a standard base64 string safe to drop into a URL query param.
// We deliberately do NOT use lz-string's compressToEncodedURIComponent here:
// its alphabet includes "+", which URLSearchParams silently turns into a space
// on read, corrupting the payload. base64url ("-"/"_", no padding) is immune.
const urlSafe = (b64) =>
  b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const fromUrlSafe = (str) => {
  let b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return b64;
};

// Build the compact payload (short keys) from the generator's rich state.
export const buildTicketPayload = ({
  store = {},
  ticket = {},
  customer = {},
  loan = {},
  ornaments = [],
  isReceipt = false,
}) => ({
  // The payload is kept as SMALL as possible so the QR stays a low version with
  // large, coarse modules that even weak (Android) phone cameras can resolve.
  // Anything not essential to verifying the ticket on-scan is deliberately
  // dropped here:
  //   • store logo image URL  — long, poorly-compressible (biggest offender)
  //   • store address / email — long free-text; shop name + phone + PBL suffice
  //   • customer address       — name + mobile identify the ticket
  //   • loan scheme, ticket time — non-essential free-text
  // The viewer hides any field that comes back empty, so nothing breaks.
  s: {
    n: store.name || "",
    p: store.phone || "",
    l: store.pblId || "",
  },
  t: {
    v: ticket.voucherNo || "",
    d: ticket.date || "",
    ty: isReceipt ? "receipt" : "payment",
  },
  c: {
    n: customer.name || "",
    m: customer.mobile || "",
  },
  l: {
    pr: Number(loan.principal || 0),
    iv: Number(loan.itemValue || 0),
    ir: Number(loan.interestRate || 0),
    ia: Number(loan.interestAmount || 0),
    pd: Number(loan.paidAmount || 0),
  },
  o: (ornaments || []).map((it) => ({
    n: it.name || "",
    p: Number(it.pcs || 0),
    g: Number(it.grossWeight || 0),
    v: Number(it.value || 0),
  })),
});

// Encode payload -> compressed, URL-safe string for the ?d= param.
// LZ-compression keeps the QR low-density enough to scan on weaker (Android)
// cameras even with the store image URL included.
export const encodeTicketData = (payload) =>
  urlSafe(LZString.compressToBase64(JSON.stringify(payload)));

// Decode the ?d= param -> a normalized, human-friendly ticket object.
export const decodeTicketData = (encoded) => {
  const json = LZString.decompressFromBase64(fromUrlSafe(encoded));
  if (!json) throw new Error("Failed to decompress ticket data");
  const raw = JSON.parse(json);
  const s = raw.s || {};
  const t = raw.t || {};
  const c = raw.c || {};
  const l = raw.l || {};
  return {
    store: {
      name: s.n || "",
      address: s.a || "",
      phone: s.p || "",
      email: s.e || "",
      pblId: s.l || "",
      // Not carried in the QR anymore (see buildTicketPayload); viewer shows a
      // storefront icon fallback when empty.
      image: s.img || "",
    },
    ticket: {
      voucherNo: t.v || "",
      date: t.d || "",
      time: t.tm || "",
      type: t.ty || "payment",
    },
    customer: {
      name: c.n || "",
      mobile: c.m || "",
      address: c.a || "",
    },
    loan: {
      principal: Number(l.pr || 0),
      itemValue: Number(l.iv || 0),
      interestRate: Number(l.ir || 0),
      interestAmount: Number(l.ia || 0),
      paidAmount: Number(l.pd || 0),
      scheme: l.sc || "",
    },
    ornaments: (raw.o || []).map((it) => ({
      name: it.n || "",
      pcs: Number(it.p || 0),
      grossWeight: Number(it.g || 0),
      value: Number(it.v || 0),
    })),
  };
};
